import { useCallback, useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import CircularProgress from '@mui/material/CircularProgress';
import { styled } from '@mui/material/styles';
import { CalendarDays, ChevronLeft, ChevronRight, Clock3, TriangleAlert, Plane, Users } from 'lucide-react';
import api from '../services/api';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import { useJob } from '../context/JobContext';
import { useNavigate } from 'react-router-dom';

const StyledCard = styled(Card)(() => ({
  boxShadow: '0 0 2px 0 rgba(145, 158, 171, 0.2), 0 12px 24px -4px rgba(145, 158, 171, 0.12)',
  borderRadius: 16,
  overflow: 'hidden'
}));

const WEEK_DAYS = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'];

const KPI_META = {
  oreTotali: { title: 'Ore lavorate', icon: Clock3, color: '#22c55e' },
  giorniPresenza: { title: 'Giorni presenza', icon: Users, color: '#0ea5e9' },
  giorniFerie: { title: 'Giorni ferie', icon: Plane, color: '#6366f1' },
  anomalieAperte: { title: 'Anomalie aperte', icon: TriangleAlert, color: '#f59e0b' },
  giorniMalattia: { title: 'Giorni malattia', icon: CalendarDays, color: '#ef4444' }
};

function KpiTile({ title, value, icon: Icon, color }) {
  return (
    <StyledCard sx={{ p: 2, height: '100%', transition: 'all .2s ease', '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 12px 26px -10px rgba(15, 23, 42, 0.28)' } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Typography variant="body2" color="text.secondary">{title}</Typography>
        <Box
          sx={{
            width: 30,
            height: 30,
            borderRadius: '50%',
            bgcolor: `${color}1A`,
            color,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <Icon size={16} />
        </Box>
      </Box>
      <Typography variant="h5" sx={{ fontWeight: 800, mt: 0.75 }}>{value}</Typography>
    </StyledCard>
  );
}

function toIsoDate(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

function formatDateIt(isoDate) {
  if (!isoDate) return '-';
  const p = String(isoDate).slice(0, 10).split('-');
  if (p.length !== 3) return String(isoDate);
  return `${p[2]}/${p[1]}/${p[0]}`;
}

function formatHours(value) {
  if (value === null || value === undefined || value === '') return '00:00';
  const n = Number(value);
  if (!Number.isNaN(n)) {
    const total = Math.max(0, Math.round(n * 60));
    return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
  }
  const str = String(value);
  if (/^\d{2}:\d{2}/.test(str)) return str.slice(0, 5);
  return str;
}

function enumerateIsoDays(startIso, endIso) {
  const out = [];
  const start = new Date(`${startIso}T00:00:00`);
  const end = new Date(`${endIso}T00:00:00`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || start > end) return out;
  const cur = new Date(start);
  while (cur <= end) {
    out.push(toIsoDate(cur));
    cur.setDate(cur.getDate() + 1);
  }
  return out;
}

function isWeekendIso(iso) {
  const d = new Date(`${String(iso).slice(0, 10)}T00:00:00`);
  if (Number.isNaN(d.getTime())) return false;
  const w = d.getDay();
  return w === 0 || w === 6;
}

export default function HrPresenzeMensili() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addJob } = useJob();
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [monthRef, setMonthRef] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });
  const [dipendentiOptions, setDipendentiOptions] = useState([]);
  const [selectedDipendenteId, setSelectedDipendenteId] = useState('');
  const [presenzeRows, setPresenzeRows] = useState([]);
  const [anomalieRows, setAnomalieRows] = useState([]);
  const [richiesteRows, setRichiesteRows] = useState([]);
  const [selectedDayIso, setSelectedDayIso] = useState('');
  const [confirmGiustificaOpen, setConfirmGiustificaOpen] = useState(false);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [reportMonth, setReportMonth] = useState(() => String(new Date().getMonth() + 1));
  const [reportYear, setReportYear] = useState(() => String(new Date().getFullYear()));
  const [reportSendEmail, setReportSendEmail] = useState(false);
  const [reportRecipientEmail, setReportRecipientEmail] = useState('');
  const [reportLoading, setReportLoading] = useState(false);
  const [earlyReportDialogOpen, setEarlyReportDialogOpen] = useState(false);
  const [earlyReportFrom, setEarlyReportFrom] = useState('');
  const [earlyReportTo, setEarlyReportTo] = useState('');
  const [earlyReportKind, setEarlyReportKind] = useState('anticipi_ingresso');
  const [earlyReportMode, setEarlyReportMode] = useState('dettaglio');
  const [earlyReportLoading, setEarlyReportLoading] = useState(false);

  const isManageUser = useMemo(() => {
    const role = String(user?.role || '').toLowerCase();
    const dep = String(user?.department_name || '').toLowerCase();
    return role === 'admin' || dep.includes('hr') || dep.includes('risorse umane') || dep.includes('personale');
  }, [user?.department_name, user?.role]);
  const todayIso = useMemo(() => toIsoDate(new Date()), []);

  const monthRange = useMemo(() => {
    const [y, m] = monthRef.split('-').map(Number);
    const start = new Date(y, m - 1, 1);
    const end = new Date(y, m, 0);
    return { start, end, dataDa: toIsoDate(start), dataA: toIsoDate(end) };
  }, [monthRef]);

  const monthLabel = useMemo(
    () => new Date(`${monthRange.dataDa}T00:00:00`).toLocaleDateString('it-IT', { month: 'long', year: 'numeric' }),
    [monthRange.dataDa]
  );

  const loadDipendenti = useCallback(async () => {
    if (!isManageUser) return;
    try {
      const res = await api.get('/dipendenti', { params: { limit: 300, offset: 0, sortBy: 'cognome', sortDir: 'ASC' } });
      const opts = (res.data?.items || []).map((d) => ({ id: String(d.id), label: `${d.cognome || ''} ${d.nome || ''}`.trim() }));
      setDipendentiOptions(opts);
      if (!selectedDipendenteId && opts.length) setSelectedDipendenteId(opts[0].id);
    } catch (error) {
      console.error('Errore caricamento dipendenti:', error);
      setDipendentiOptions([]);
    }
  }, [isManageUser, selectedDipendenteId]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        data_da: monthRange.dataDa,
        data_a: monthRange.dataA
      };
      if (isManageUser && selectedDipendenteId) {
        params.dipendente_id = selectedDipendenteId;
      }
      const [presRes, anomRes, richRes] = await Promise.all([
        api.get('/presenze-giornaliere', { params }),
        api.get('/anomalie', { params }),
        api.get('/richieste-assenza', { params: { ...params, stato: 'approvata' } })
      ]);
      setPresenzeRows(presRes.data || []);
      setAnomalieRows(anomRes.data || []);
      setRichiesteRows(richRes.data || []);
    } catch (error) {
      console.error('Errore caricamento presenze mensili:', error);
      toast.error(error.response?.data?.message || 'Errore caricamento vista mensile');
      setPresenzeRows([]);
      setAnomalieRows([]);
      setRichiesteRows([]);
    } finally {
      setLoading(false);
    }
  }, [isManageUser, monthRange.dataA, monthRange.dataDa, selectedDipendenteId]);

  useEffect(() => {
    loadDipendenti();
  }, [loadDipendenti]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    const firstDay = `${monthRef}-01`;
    setSelectedDayIso(firstDay);
  }, [monthRef]);

  const presenzeByDate = useMemo(() => {
    const map = new Map();
    presenzeRows.forEach((row) => map.set(String(row.data_lavoro).slice(0, 10), row));
    return map;
  }, [presenzeRows]);

  const anomalieByDate = useMemo(() => {
    const map = new Map();
    anomalieRows.forEach((row) => {
      const key = String(row.data_lavoro).slice(0, 10);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(row);
    });
    return map;
  }, [anomalieRows]);

  const malattiaDaysSet = useMemo(() => {
    const set = new Set();
    const monthStart = monthRange.dataDa;
    const monthEnd = monthRange.dataA;
    richiesteRows.forEach((r) => {
      const code = String(r.tipo_assenza_codice || '').toLowerCase();
      const name = String(r.tipo_assenza_nome || '').toLowerCase();
      const isMalattia = code.includes('malatt') || name.includes('malatt');
      if (!isMalattia) return;
      const start = String(r.data_inizio || '').slice(0, 10);
      const end = String(r.data_fine || r.data_inizio || '').slice(0, 10);
      if (!start) return;
      const overlapStart = start > monthStart ? start : monthStart;
      const overlapEnd = end < monthEnd ? end : monthEnd;
      enumerateIsoDays(overlapStart, overlapEnd).forEach((d) => set.add(d));
    });
    return set;
  }, [monthRange.dataA, monthRange.dataDa, richiesteRows]);

  const ferieDaysSet = useMemo(() => {
    const set = new Set();
    const monthStart = monthRange.dataDa;
    const monthEnd = monthRange.dataA;
    richiesteRows.forEach((r) => {
      const code = String(r.tipo_assenza_codice || '').toLowerCase();
      const name = String(r.tipo_assenza_nome || '').toLowerCase();
      const isFerie = code.includes('ferie') || name.includes('ferie');
      if (!isFerie) return;
      const start = String(r.data_inizio || '').slice(0, 10);
      const end = String(r.data_fine || r.data_inizio || '').slice(0, 10);
      if (!start) return;
      const overlapStart = start > monthStart ? start : monthStart;
      const overlapEnd = end < monthEnd ? end : monthEnd;
      enumerateIsoDays(overlapStart, overlapEnd).forEach((d) => {
        if (isWeekendIso(d)) return;
        set.add(d);
      });
    });
    return set;
  }, [monthRange.dataA, monthRange.dataDa, richiesteRows]);

  const daysGrid = useMemo(() => {
    const start = monthRange.start;
    const end = monthRange.end;
    const firstWeekdayMon0 = (start.getDay() + 6) % 7;
    const days = [];
    for (let i = 0; i < firstWeekdayMon0; i += 1) days.push(null);
    for (let d = 1; d <= end.getDate(); d += 1) {
      const iso = `${monthRef}-${String(d).padStart(2, '0')}`;
      const pres = presenzeByDate.get(iso) || null;
      const anom = anomalieByDate.get(iso) || [];
      const dayDate = new Date(`${iso}T00:00:00`);
      const weekDay = dayDate.getDay(); // dom=0 ... sab=6
      const isWeekend = weekDay === 0 || weekDay === 6;
      const hasOpenAnomaly = anom.some((a) => a.stato === 'aperta');
      const isMalattia = malattiaDaysSet.has(iso);
      const isFerie = !isWeekend && (ferieDaysSet.has(iso) || pres?.stato_presenza === 'in_ferie');
      const isPresente = pres?.stato_presenza === 'presente' || pres?.stato_presenza === 'anomalia';
      const isNonLavorativo = pres?.stato_presenza === 'non_previsto_da_orario' || (!pres && isWeekend);
      const isAssente = !isPresente && !isFerie && !isMalattia && !isNonLavorativo;

      let stato = 'assente';
      if (hasOpenAnomaly) stato = 'anomalia';
      else if (isMalattia) stato = 'malattia';
      else if (isFerie) stato = 'in_ferie';
      else if (isNonLavorativo) stato = 'non_lavorativo';
      else if (isPresente) stato = 'presente';

      days.push({
        iso,
        day: d,
        presenza: pres,
        anomalie: anom,
        stato,
        showPresenteWithAnomalia: hasOpenAnomaly && isPresente,
        showAssenteBadge: stato === 'assente' && isAssente
      });
    }
    return days;
  }, [anomalieByDate, ferieDaysSet, malattiaDaysSet, monthRange.end, monthRange.start, monthRef, presenzeByDate]);

  const kpi = useMemo(() => {
    let oreTotaliMin = 0;
    let giorniPresenza = 0;
    const giorniFerie = ferieDaysSet.size;
    const giorniMalattia = malattiaDaysSet.size;
    presenzeRows.forEach((row) => {
      const mins = Math.round(Number(row.ore_lavorate || 0) * 60);
      oreTotaliMin += Number.isNaN(mins) ? 0 : Math.max(0, mins);
      if (row.stato_presenza === 'presente' || row.stato_presenza === 'anomalia') giorniPresenza += 1;
    });
    const anomalieAperte = anomalieRows.filter((a) => a.stato === 'aperta').length;
    return {
      oreTotali: `${Math.floor(oreTotaliMin / 60)}h ${String(oreTotaliMin % 60).padStart(2, '0')}m`,
      giorniPresenza,
      giorniFerie,
      giorniMalattia,
      anomalieAperte
    };
  }, [anomalieRows, ferieDaysSet, malattiaDaysSet, presenzeRows]);

  const selectedPresenza = presenzeByDate.get(selectedDayIso) || null;
  const selectedAnomalie = anomalieByDate.get(selectedDayIso) || [];
  const selectedOpenAnomalie = selectedAnomalie.filter((a) => a.stato === 'aperta');

  const moveMonth = (delta) => {
    const [y, m] = monthRef.split('-').map(Number);
    const d = new Date(y, m - 1 + delta, 1);
    setMonthRef(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  };

  const selectedDip = dipendentiOptions.find((d) => d.id === selectedDipendenteId) || null;

  const dayBadge = (stato) => {
    if (stato === 'presente') return { label: 'Presente', bg: '#ecfdf3', color: '#15803d' };
    if (stato === 'in_ferie') return { label: 'Ferie', bg: '#eef2ff', color: '#4f46e5' };
    if (stato === 'anomalia') return { label: 'Anomalia', bg: '#fff7ed', color: '#c2410c' };
    if (stato === 'malattia') return { label: 'Malattia', bg: '#fef2f2', color: '#b91c1c' };
    if (stato === 'non_lavorativo') return { label: 'Riposo', bg: '#f1f5f9', color: '#475569' };
    if (stato === 'assente') return { label: 'Assente', bg: '#f3f4f6', color: '#6b7280' };
    return null;
  };

  const handleRigeneraSelectedDay = async () => {
    if (!isManageUser) return;
    if (!selectedDayIso) return;
    try {
      setActionLoading(true);
      const payload = { data_lavoro: selectedDayIso };
      if (selectedDipendenteId) payload.dipendente_id = Number(selectedDipendenteId);
      await api.post('/presenze-giornaliere/rigenera', payload);
      toast.success('Rigenerazione giornata completata');
      await loadData();
    } catch (error) {
      console.error('Errore rigenerazione giornata:', error);
      toast.error(error.response?.data?.message || 'Errore rigenerazione giornata');
    } finally {
      setActionLoading(false);
    }
  };

  const handleGiustificaAperteDelGiorno = async () => {
    if (!isManageUser) return;
    if (!selectedOpenAnomalie.length) return;
    try {
      setActionLoading(true);
      await Promise.all(selectedOpenAnomalie.map((a) => api.post(`/anomalie/${a.id}/giustifica`, {})));
      toast.success('Anomalie aperte giustificate');
      await loadData();
    } catch (error) {
      console.error('Errore giustificazione anomalie:', error);
      toast.error(error.response?.data?.message || 'Errore giustificazione anomalie');
    } finally {
      setActionLoading(false);
    }
  };

  const openReportDialog = () => {
    const [y, m] = monthRef.split('-');
    setReportMonth(String(Number(m)));
    setReportYear(String(Number(y)));
    setReportSendEmail(false);
    setReportRecipientEmail(String(user?.email || ''));
    setReportDialogOpen(true);
  };

  const openEarlyReportDialog = () => {
    setEarlyReportFrom(monthRange.dataDa);
    setEarlyReportTo(monthRange.dataA);
    setEarlyReportKind('anticipi_ingresso');
    setEarlyReportMode('dettaglio');
    setEarlyReportDialogOpen(true);
  };

  const handleStartReportAssenze = async () => {
    const month = Number(reportMonth);
    const year = Number(reportYear);
    const recipient = String(reportRecipientEmail || '').trim();
    if (!Number.isFinite(month) || month < 1 || month > 12) {
      toast.error('Mese non valido');
      return;
    }
    if (!Number.isFinite(year) || year < 2000 || year > 2100) {
      toast.error('Anno non valido');
      return;
    }
    if (reportSendEmail) {
      const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(recipient);
      if (!emailOk) {
        toast.error('Inserisci una email destinatario valida');
        return;
      }
    }
    try {
      setReportLoading(true);
      const res = await api.post('/reports/hr-assenze', {
        month,
        year,
        send_email: reportSendEmail,
        recipient_email: reportSendEmail ? recipient : ''
      });
      const jobId = res.data?.jobId;
      if (!jobId) {
        throw new Error('Job ID non ricevuto');
      }
      addJob(jobId, 'hr_absence_report');
      setReportDialogOpen(false);
      toast.success('Generazione report assenze avviata');
    } catch (error) {
      console.error('Errore avvio report assenze:', error);
      toast.error(error.response?.data?.message || error.message || 'Errore avvio report assenze');
    } finally {
      setReportLoading(false);
    }
  };

  const handleStartEarlyEntryReport = async () => {
    const from = String(earlyReportFrom || '').trim();
    const to = String(earlyReportTo || '').trim();
    const kind = String(earlyReportKind || '').trim();
    const mode = String(earlyReportMode || '').trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(from)) {
      toast.error('Data da non valida');
      return;
    }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(to)) {
      toast.error('Data a non valida');
      return;
    }
    if (from > to) {
      toast.error('Intervallo non valido: la data da è successiva alla data a');
      return;
    }
    if (kind !== 'anticipi_ingresso' && kind !== 'posticipi_uscita') {
      toast.error('Tipo report non valido');
      return;
    }
    if (mode !== 'dettaglio' && mode !== 'mensile' && mode !== 'sintetico') {
      toast.error('Modalità report non valida');
      return;
    }
    try {
      setEarlyReportLoading(true);
      const res = await api.post('/reports/hr-anticipi-posticipi', {
        data_da: from,
        data_a: to,
        kind,
        mode
      });
      const jobId = res.data?.jobId;
      if (!jobId) {
        throw new Error('Job ID non ricevuto');
      }
      addJob(jobId, 'hr_time_variance_report');
      setEarlyReportDialogOpen(false);
      toast.success('Generazione report anticipi/posticipi avviata');
    } catch (error) {
      console.error('Errore avvio report anticipi/posticipi:', error);
      toast.error(error.response?.data?.message || error.message || 'Errore avvio report anticipi/posticipi');
    } finally {
      setEarlyReportLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
        <Box sx={{ width: 40, height: 40, borderRadius: '50%', bgcolor: '#eef2ff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CalendarDays size={20} color="#4f46e5" />
        </Box>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 'bold' }}>Presenze Mensili</Typography>
          <Typography variant="body2" color="text.secondary">Calendario presenze e anomalie mese per mese.</Typography>
        </Box>
      </Box>

      <Grid container spacing={2} columns={{ xs: 12, md: 20 }} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={4}>
          <KpiTile {...KPI_META.oreTotali} value={kpi.oreTotali} />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <KpiTile {...KPI_META.giorniPresenza} value={kpi.giorniPresenza} />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <KpiTile {...KPI_META.giorniFerie} value={kpi.giorniFerie} />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <KpiTile {...KPI_META.anomalieAperte} value={kpi.anomalieAperte} />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <KpiTile {...KPI_META.giorniMalattia} value={kpi.giorniMalattia} />
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} lg={8}>
          <StyledCard sx={{ p: 2 }}>
            {loading ? (
              <Box sx={{ py: 8, textAlign: 'center' }}><CircularProgress /></Box>
            ) : (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.25 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, textTransform: 'capitalize' }}>
                    {monthLabel}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    {isManageUser && (
                      <Box sx={{ minWidth: { xs: 210, sm: 260 }, maxWidth: 320 }}>
                        <Autocomplete
                          size="small"
                          options={dipendentiOptions}
                          value={selectedDip}
                          getOptionLabel={(option) => option.label || ''}
                          isOptionEqualToValue={(o, v) => String(o.id) === String(v.id)}
                          onChange={(_, val) => setSelectedDipendenteId(val ? String(val.id) : '')}
                          renderInput={(params) => <TextField {...params} label="Dipendente" />}
                        />
                      </Box>
                    )}
                    <Button size="small" variant="outlined" onClick={() => moveMonth(-1)} sx={{ minWidth: 30, px: 0.25 }}>
                      <ChevronLeft size={14} />
                    </Button>
                    <Button size="small" variant="outlined" onClick={() => moveMonth(1)} sx={{ minWidth: 30, px: 0.25 }}>
                      <ChevronRight size={14} />
                    </Button>
                    {isManageUser && (
                      <Button size="small" variant="contained" onClick={openReportDialog}>
                        Report Assenze
                      </Button>
                    )}
                    {isManageUser && (
                      <Button size="small" variant="outlined" onClick={openEarlyReportDialog}>
                        Report Anticipi/Posticipi
                      </Button>
                    )}
                  </Box>
                </Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0,1fr))', gap: 0.75, mb: 0.75 }}>
                  {WEEK_DAYS.map((d) => (
                    <Typography key={d} variant="caption" color="text.secondary" sx={{ fontWeight: 600, textAlign: 'center' }}>{d}</Typography>
                  ))}
                </Box>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                  <Chip size="small" label="Presente" sx={{ bgcolor: '#ecfdf3', color: '#15803d' }} />
                  <Chip size="small" label="Ferie" sx={{ bgcolor: '#eef2ff', color: '#4f46e5' }} />
                  <Chip size="small" label="Malattia" sx={{ bgcolor: '#fef2f2', color: '#b91c1c' }} />
                  <Chip size="small" label="Anomalia" sx={{ bgcolor: '#fff7ed', color: '#c2410c' }} />
                  <Chip size="small" label="Riposo" sx={{ bgcolor: '#f1f5f9', color: '#475569' }} />
                  <Chip size="small" label="Assente" sx={{ bgcolor: '#f3f4f6', color: '#6b7280' }} />
                </Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0,1fr))', gap: 0.75 }}>
                  {daysGrid.map((item, idx) => (
                    item ? (
                      <Box
                        key={item.iso}
                        onClick={() => setSelectedDayIso(item.iso)}
                        sx={{
                          border: '1px solid rgba(145, 158, 171, 0.24)',
                          borderRadius: 1.25,
                          p: 0.75,
                          minHeight: 64,
                          cursor: 'pointer',
                          bgcolor: selectedDayIso === item.iso ? 'rgba(14,165,233,0.08)' : 'transparent',
                          color: 'inherit',
                          borderColor: selectedDayIso === item.iso ? 'rgba(14,165,233,0.7)' : 'rgba(145, 158, 171, 0.24)',
                          boxShadow: selectedDayIso === item.iso ? '0 6px 16px rgba(14,165,233,0.20)' : 'none',
                          outline: item.iso === todayIso && selectedDayIso !== item.iso ? '1px solid rgba(14,165,233,0.6)' : 'none',
                          transition: 'all .2s ease',
                          '&:hover': {
                            transform: 'translateY(-1px)',
                            bgcolor: 'rgba(14,165,233,0.12)'
                          }
                        }}
                      >
                        <Typography variant="caption" sx={{ fontWeight: 700, lineHeight: 1 }}>{item.day}</Typography>
                        <Box sx={{ mt: 0.5, minHeight: 18 }}>
                          {item.showPresenteWithAnomalia ? (
                            <Chip size="small" label="Presente" sx={{ bgcolor: '#ecfdf3', color: '#15803d', height: 18 }} />
                          ) : (item.stato === 'presente' && (
                            <Typography variant="caption" sx={{ opacity: 0.8 }}>
                              {formatHours(item.presenza?.ore_lavorate)}
                            </Typography>
                          ))}
                        </Box>
                        {(item.stato !== 'assente' || item.showAssenteBadge) && dayBadge(item.stato) && (
                          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 0.25 }}>
                            <Box
                              sx={{
                                px: 0.75,
                                py: 0.1,
                                borderRadius: 1.5,
                                fontSize: 10,
                                fontWeight: 700,
                                bgcolor: dayBadge(item.stato).bg,
                                color: dayBadge(item.stato).color
                              }}
                            >
                              {dayBadge(item.stato).label}
                            </Box>
                          </Box>
                        )}
                      </Box>
                    ) : (
                      <Box key={`empty-${idx}`} />
                    )
                  ))}
                </Box>
              </>
            )}
          </StyledCard>
        </Grid>
        <Grid item xs={12} lg={4}>
          <StyledCard sx={{ p: 2 }}>
            <Typography variant="h6">Dettaglio giorno</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {formatDateIt(selectedDayIso)}
            </Typography>
            <Typography variant="body2">
              Prima entrata: <strong>{selectedPresenza?.prima_entrata ? String(selectedPresenza.prima_entrata).slice(0, 5) : '-'}</strong>
            </Typography>
            <Typography variant="body2">
              Ultima uscita: <strong>{selectedPresenza?.ultima_uscita ? String(selectedPresenza.ultima_uscita).slice(0, 5) : '-'}</strong>
            </Typography>
            <Typography variant="body2">
              Ore lavorate: <strong>{formatHours(selectedPresenza?.ore_lavorate)}</strong>
            </Typography>
            <Typography variant="body2">
              Stato: <strong>{selectedPresenza?.stato_presenza || 'assente'}</strong>
            </Typography>

            <Box sx={{ mt: 2, display: 'grid', gap: 1 }}>
              <Chip icon={<TriangleAlert size={14} />} label={`Anomalie: ${selectedAnomalie.length}`} variant="outlined" color={selectedAnomalie.some((a) => a.stato === 'aperta') ? 'warning' : 'default'} />
              <Chip icon={<Clock3 size={14} />} label={`Ore previste: ${formatHours(selectedPresenza?.ore_previste)}`} variant="outlined" color="info" />
              <Chip icon={<Plane size={14} />} label={`Ferie: ${ferieDaysSet.has(selectedDayIso) || selectedPresenza?.stato_presenza === 'in_ferie' ? 'Si' : 'No'}`} variant="outlined" color={ferieDaysSet.has(selectedDayIso) || selectedPresenza?.stato_presenza === 'in_ferie' ? 'success' : 'default'} />
              <Chip icon={<CalendarDays size={14} />} label={`Malattia: ${malattiaDaysSet.has(selectedDayIso) ? 'Si' : 'No'}`} variant="outlined" color={malattiaDaysSet.has(selectedDayIso) ? 'error' : 'default'} />
            </Box>

            <Box sx={{ mt: 2 }}>
              {selectedAnomalie.length === 0 ? (
                <Typography variant="caption" color="text.secondary">Nessuna anomalia in questa data.</Typography>
              ) : selectedAnomalie.map((a) => (
                <Box key={a.id} sx={{ mb: 1, p: 1, border: '1px solid rgba(145, 158, 171, 0.24)', borderRadius: 1 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700 }}>{a.tipo_anomalia}</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    gravita {a.gravita} - stato {a.stato}
                  </Typography>
                </Box>
              ))}
            </Box>
            <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button
                size="small"
                variant="outlined"
                onClick={() => navigate(`/hr/timbra?date=${selectedDayIso}${isManageUser && selectedDipendenteId ? `&dipendente_id=${selectedDipendenteId}` : ''}`)}
              >
                Vedi timbrature del giorno
              </Button>
            </Box>
            {isManageUser && (
              <Box sx={{ mt: 1, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                <Button size="small" variant="outlined" fullWidth onClick={handleRigeneraSelectedDay} disabled={actionLoading}>
                  {actionLoading ? 'Rigenerazione...' : 'Rigenera giornata'}
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  fullWidth
                  onClick={() => setConfirmGiustificaOpen(true)}
                  disabled={actionLoading || selectedOpenAnomalie.length === 0}
                >
                  Giustifica aperte ({selectedOpenAnomalie.length})
                </Button>
              </Box>
            )}
          </StyledCard>
        </Grid>
      </Grid>

      <Dialog open={confirmGiustificaOpen} onClose={() => setConfirmGiustificaOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Conferma Giustificazione</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2">
            Confermi la giustificazione di <strong>{selectedOpenAnomalie.length}</strong> anomalie aperte del giorno{' '}
            <strong>{formatDateIt(selectedDayIso)}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmGiustificaOpen(false)} disabled={actionLoading}>Annulla</Button>
          <Button
            variant="contained"
            onClick={async () => {
              await handleGiustificaAperteDelGiorno();
              setConfirmGiustificaOpen(false);
            }}
            disabled={actionLoading || selectedOpenAnomalie.length === 0}
          >
            {actionLoading ? 'Elaborazione...' : 'Conferma'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={reportDialogOpen}
        onClose={() => {
          if (!reportLoading) setReportDialogOpen(false);
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Report Assenze</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'grid', gap: 1.25, mt: 0.5 }}>
            <TextField
              label="Mese"
              type="number"
              size="small"
              value={reportMonth}
              onChange={(e) => setReportMonth(e.target.value)}
              inputProps={{ min: 1, max: 12 }}
            />
            <TextField
              label="Anno"
              type="number"
              size="small"
              value={reportYear}
              onChange={(e) => setReportYear(e.target.value)}
              inputProps={{ min: 2000, max: 2100 }}
            />
            <FormControlLabel
              control={(
                <Checkbox
                  checked={reportSendEmail}
                  onChange={(e) => setReportSendEmail(e.target.checked)}
                />
              )}
              label="Invia anche via email"
            />
            <TextField
              label="Email destinatario"
              size="small"
              type="email"
              value={reportRecipientEmail}
              onChange={(e) => setReportRecipientEmail(e.target.value)}
              disabled={!reportSendEmail}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReportDialogOpen(false)} disabled={reportLoading}>Annulla</Button>
          <Button variant="contained" onClick={handleStartReportAssenze} disabled={reportLoading}>
            {reportLoading ? 'Avvio...' : 'Genera report'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={earlyReportDialogOpen}
        onClose={() => {
          if (!earlyReportLoading) setEarlyReportDialogOpen(false);
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Report Anticipi/Posticipi</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'grid', gap: 1.25, mt: 0.5 }}>
            <FormControl>
              <FormLabel>Tipo report</FormLabel>
              <RadioGroup
                value={earlyReportKind}
                onChange={(e) => setEarlyReportKind(e.target.value)}
              >
                <FormControlLabel
                  value="anticipi_ingresso"
                  control={<Radio size="small" />}
                  label="Anticipi orario (ingresso)"
                />
                <FormControlLabel
                  value="posticipi_uscita"
                  control={<Radio size="small" />}
                  label="Posticipi orario (uscita)"
                />
              </RadioGroup>
            </FormControl>
            <FormControl>
              <FormLabel>Modalità</FormLabel>
              <RadioGroup
                value={earlyReportMode}
                onChange={(e) => setEarlyReportMode(e.target.value)}
              >
                <FormControlLabel
                  value="dettaglio"
                  control={<Radio size="small" />}
                  label="Dettaglio"
                />
                <FormControlLabel
                  value="mensile"
                  control={<Radio size="small" />}
                  label="Riepilogo mensile + finale"
                />
                <FormControlLabel
                  value="sintetico"
                  control={<Radio size="small" />}
                  label="Solo riepilogo finale"
                />
              </RadioGroup>
            </FormControl>
            <TextField
              label="Da"
              type="date"
              size="small"
              value={earlyReportFrom}
              onChange={(e) => setEarlyReportFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="A"
              type="date"
              size="small"
              value={earlyReportTo}
              onChange={(e) => setEarlyReportTo(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEarlyReportDialogOpen(false)} disabled={earlyReportLoading}>Annulla</Button>
          <Button variant="contained" onClick={handleStartEarlyEntryReport} disabled={earlyReportLoading}>
            {earlyReportLoading ? 'Avvio...' : 'Genera report'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
